import { useLayoutEffect, useRef, type InputHTMLAttributes, type Ref } from 'react';
import { currencySymbol } from '../lib/format.ts';

/** A typed amount as a number: "£15,700.00" → 15700; null when empty, NaN when it is not a number. */
export const parseAmount = (text: string): number | null => {
  const trimmed = text.replace(/[£$,\s]/g, '');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : Number.NaN;
};

/** What the field shows while typing: digits grouped in thousands, at most two decimals, a leading minus kept. */
export function groupedAmount(text: string): string {
  const cleaned = text.replace(/[^\d.-]/g, '');
  const minus = cleaned.startsWith('-') ? '-' : '';
  const [whole = '', ...rest] = cleaned.replace(/-/g, '').split('.');
  const grouped = whole.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${minus}${grouped}${rest.length > 0 ? `.${rest.join('').slice(0, 2)}` : ''}`;
}

/** The decimal string a grouped amount stands for: "15,700.5" → "15700.5". */
export const ungroupedAmount = (text: string): string => text.replace(/,/g, '');

type NativeProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'className' | 'ref' | 'type' | 'inputMode'>;

/**
 * A money field in the plan's currency that keeps its thousands separators while typing, with the
 * caret where the person left it. `value` is the grouped text and `onChange` hands back the grouped
 * text; `block` names the wrapper's class and its `__symbol` and `__input` parts.
 */
export function AmountInput({ value, onChange, block = 'amount-input', className, inputRef, ...native }: NativeProps & {
  value: string;
  onChange: (text: string) => void;
  block?: string;
  className?: string | undefined;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const local = useRef<HTMLInputElement | null>(null);
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = local.current;
    if (!el || caret.current === null || document.activeElement !== el) return;
    let digits = caret.current;
    let at = 0;
    while (at < el.value.length && digits > 0) {
      if (/[\d.-]/.test(el.value[at]!)) digits -= 1;
      at += 1;
    }
    el.setSelectionRange(at, at);
    caret.current = null;
  }, [value]);
  const attach = (el: HTMLInputElement | null) => {
    local.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) inputRef.current = el;
  };
  return (
    <span className={className ? `${block} ${className}` : block}>
      <span className={`${block}__symbol`} aria-hidden="true">{currencySymbol()}</span>
      <input
        {...native}
        ref={attach}
        type="text"
        className={`${block}__input`}
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => {
          const raw = e.target.value;
          const before = raw.slice(0, e.target.selectionStart ?? raw.length);
          caret.current = before.replace(/[^\d.-]/g, '').length;
          onChange(groupedAmount(raw));
        }}
      />
    </span>
  );
}
